/**
 * TrendingWidgetPositioner.ts
 * Module for positioning trending widget after ad element
 */
import { logger } from 'utils/system/logger'
import { isDomReady } from 'utils/dom'

/**
 * Configuration constants for trending widget positioning
 */
const CONFIG = {
    SELECTORS: {
        AD_ELEMENT:
            '.ad__section-border.article-content__ad-group.ad_counter_2',
        TRENDING_WIDGET: '.widget.article-content__widget-group--trending-slot',
        HEADER_ELEMENT: 'header.article-header'
    },
    GRID: {
        AD_OFFSET: 3
    },
    TIMEOUT: {
        OBSERVER_MS: 5000
    }
}

/**
 * This class fixes a layout issue with the trending widget positioning.
 *
 * By default, the trending widget has a fixed position (grid-row-start: 4),
 * which works well for standard article pages where the ad_counter_2 container
 * appears right after the first content section. In this normal case, the trending
 * widget is perfectly positioned below the ad.
 *
 * However, when specific ad configuration settings are enabled (especially when
 * "Characters before first ad in story" is configured), multiple content sections
 * can appear before the ad_counter_2 container. With the fixed positioning, the
 * trending widget would appear too early in the page layout, creating an unwanted
 * gap in the content area.
 *
 * This class dynamically calculates the correct position for the trending widget
 * by counting the elements between the header and the ad, ensuring the widget
 * always appears directly after the ad_counter_2 container, regardless of how
 * many content sections come before it.
 */
export class TrendingWidgetPositioner {
    private adElement: HTMLElement | undefined
    private trendingWidget: HTMLElement | undefined
    private headerElement: HTMLElement | undefined
    private observer: MutationObserver | undefined

    /**
     * Creates a new instance of TrendingWidgetPositioner
     */
    public constructor(protected el: HTMLElement) {
        logger('info', 'initializing trending widget positioner')
        this.adElement = undefined
        this.trendingWidget = undefined
        this.headerElement = undefined
        this.observer = undefined
        this.init()
    }

    /**
     * Renders trending widget in the correct position
     */
    public init(): void {
        logger('info', 'rendering trending widget positioner')
        isDomReady()
            .then(() => {
                // First check if this page is compatible
                if (!this.isCompatiblePage()) {
                    logger(
                        'info',
                        'page is not compatible with trending widget positioning - exiting'
                    )
                    return
                }

                // Try initial positioning if elements already exist
                if (this.findElements()) {
                    this.positionTrendingWidget()
                } else {
                    this.startObserver()
                }
            })
            .catch((error) => {
                logger('error', `DOM ready error: ${error.message}`)
            })
    }

    /**
     * Check if page is compatible with trending widget positioning
     * @private
     * @returns {boolean} True if page has required ad and header elements
     */
    private isCompatiblePage(): boolean {
        const adElement = document.querySelector<HTMLElement>(
            CONFIG.SELECTORS.AD_ELEMENT
        )
        const headerElement = document.querySelector<HTMLElement>(
            CONFIG.SELECTORS.HEADER_ELEMENT
        )
        if (!headerElement) {
            logger('info', 'header element not found - page not compatible')
            return false
        }
        if (!adElement) {
            logger('info', 'ad element not found - page not compatible')
            return false
        }
        return true
    }

    /**
     * Find required DOM elements
     * @private
     * @returns {boolean} True if all elements found, false otherwise
     */
    private findElements(): boolean {
        // Convert null to undefined when elements are not found
        const adElement = document.querySelector<HTMLElement>(
            CONFIG.SELECTORS.AD_ELEMENT
        )
        this.adElement = adElement || undefined

        const trendingWidget = document.querySelector<HTMLElement>(
            CONFIG.SELECTORS.TRENDING_WIDGET
        )
        this.trendingWidget = trendingWidget || undefined

        const headerElement = document.querySelector<HTMLElement>(
            CONFIG.SELECTORS.HEADER_ELEMENT
        )
        this.headerElement = headerElement || undefined

        return !!(this.adElement && this.trendingWidget && this.headerElement)
    }

    /**
     * Start DOM mutation observer to detect when elements become available
     * @private
     */
    private startObserver(): void {
        logger('info', 'starting observer for trending widget positioning')

        this.observer = new MutationObserver(() => {
            if (this.findElements()) {
                this.positionTrendingWidget()
                this.stopObserver()
            }
        })

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        // Stop observer after few seconds as backup
        setTimeout(() => {
            if (this.observer) {
                logger('info', 'observer timeout reached - stopping observer')
                this.stopObserver()
            }
        }, CONFIG.TIMEOUT.OBSERVER_MS)
    }

    /**
     * Position trending widget after ad element
     * @private
     */
    private positionTrendingWidget(): void {
        if (!this.adElement || !this.trendingWidget || !this.headerElement) {
            logger('error', 'required elements not found for positioning')
            return
        }

        try {
            // Calculate the number of elements between the header and ad
            let count = 0
            let currentElement = this.headerElement
                .nextElementSibling as HTMLElement

            while (currentElement && currentElement !== this.adElement) {
                count++
                currentElement =
                    currentElement.nextElementSibling as HTMLElement
            }

            // Position the trending widget after the ad
            const gridRowStart = count + CONFIG.GRID.AD_OFFSET

            logger('info', `calculated gridRowStart value: ${gridRowStart}`)

            // Set CSS
            this.trendingWidget.style.gridRowStart = gridRowStart.toString()

            logger('info', 'trending widget successfully repositioned')
        } catch (error) {
            logger('error', `positioning error: ${error.message}`)
        }
    }

    /**
     * Stop and disconnect observer
     * @private
     */
    private stopObserver(): void {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = undefined
        }
    }
}
